import { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Coins, User, MoreVertical, X } from 'lucide-react';
import { useEntity } from '../../contexts/EntityContext';
import { useToast } from '../ui/Toast';
import { api, Wallet, Client } from '../../lib/api';
import { isCryptoCurrency } from '../../lib/rates.js';
import { AmountInputField } from './AmountInputField';
import { OperationSuccessModal } from './OperationSuccessModal';
import { ClientSearchAutocomplete } from './ClientSearchAutocomplete';
import { TxHashField } from './TxHashField';
import { isCashierEntityAllowed } from './constants';
import type { WalletOption, SuccessData } from './types';

interface CryptoOperationFormProps {
  onSubmit: () => void;
}

// Zod schema - independent amountIn / amountOut, both crypto wallets required.
// walletIn and walletOut MAY be the same wallet (USDT↔USDT / USDC↔USDC).
const cryptoFormSchema = z.object({
  amountIn: z.string()
    .refine(val => val === '' || val === '0' || (!isNaN(parseFloat(val)) && parseFloat(val) >= 0), 'Must be a valid number'),
  amountOut: z.string()
    .refine(val => val === '' || val === '0' || (!isNaN(parseFloat(val)) && parseFloat(val) >= 0), 'Must be a valid number'),
  walletInId: z.string().min(1, 'Please select a source wallet'),
  walletOutId: z.string().min(1, 'Please select a destination wallet'),
  clientId: z.string().min(1, 'Client is required'),
  comment: z.string().optional(),
  isPendingDeal: z.boolean().optional(),
  debtAmountIn: z.string().optional(),
  debtAmountOut: z.string().optional(),
  expectedPaymentDateIn: z.string().optional(),
  expectedPaymentDateOut: z.string().optional(),
  transactionDate: z.string().optional(),
  txHashIn: z.string().optional(),
  txHashOut: z.string().optional(),
}).refine(
  (data) => {
    const amount = parseFloat(data.amountIn) || 0;
    if (data.isPendingDeal) {
      const debt = parseFloat(data.debtAmountIn || '0') || 0;
      return amount + debt > 0;
    }
    return amount > 0;
  },
  {
    message: 'Total amount must be greater than 0',
    path: ['amountIn'],
  }
).refine(
  (data) => {
    const amount = parseFloat(data.amountOut) || 0;
    if (data.isPendingDeal) {
      const debt = parseFloat(data.debtAmountOut || '0') || 0;
      return amount + debt > 0;
    }
    return amount > 0;
  },
  {
    message: 'Total amount must be greater than 0',
    path: ['amountOut'],
  }
);

type CryptoFormData = z.infer<typeof cryptoFormSchema>;

const CURRENCY_ICONS: Record<string, string> = {
  USDT: '₮',
  USDC: '$',
  BTC: '₿',
  ETH: 'Ξ',
  BNB: 'B',
  SOL: 'S',
  XRP: 'X',
};

// Only stablecoins are allowed for crypto ↔ crypto exchange per requirements.
const CRYPTO_ALLOWED = ['USDT', 'USDC'];

export function CryptoOperationForm({ onSubmit }: CryptoOperationFormProps) {
  const { t } = useTranslation();
  const { currentEntity } = useEntity();
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState<SuccessData | null>(null);

  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [walletsLoading, setWalletsLoading] = useState(true);

  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [showClientMenu, setShowClientMenu] = useState(false);
  const clientMenuRef = useRef<HTMLDivElement>(null);

  const amountInputRef = useRef<HTMLInputElement | null>(null);

  const {
    handleSubmit,
    watch,
    setValue,
    register,
    reset,
    clearErrors,
    formState: { errors },
  } = useForm<CryptoFormData>({
    resolver: zodResolver(cryptoFormSchema),
    defaultValues: {
      amountIn: '',
      amountOut: '',
      walletInId: '',
      walletOutId: '',
      clientId: '',
      comment: '',
      isPendingDeal: false,
      debtAmountIn: '',
      debtAmountOut: '',
      expectedPaymentDateIn: '',
      expectedPaymentDateOut: '',
      transactionDate: new Date().toISOString().split('T')[0],
      txHashIn: '',
      txHashOut: '',
    },
  });

  const amountIn = watch('amountIn');
  const amountOut = watch('amountOut');
  const walletInId = watch('walletInId');
  const walletOutId = watch('walletOutId');
  const isPendingDeal = watch('isPendingDeal');
  const debtAmountIn = watch('debtAmountIn');
  const debtAmountOut = watch('debtAmountOut');

  // Focus the first amount input on mount
  useEffect(() => {
    amountInputRef.current?.focus();
  }, []);

  // Fetch wallets (cashDesk filtering happens server-side — the cashier
  // only receives wallets belonging to their own cashier group).
  useEffect(() => {
    async function fetchWallets() {
      if (!currentEntity?.id) return;
      if (!isCashierEntityAllowed(currentEntity.code)) {
        setWallets([]);
        setWalletsLoading(false);
        return;
      }

      try {
        setWalletsLoading(true);
        const result = await api.admin.wallets.list(currentEntity.id);
        const filtered = result.data.filter(w =>
          w.isActive &&
          isCryptoCurrency(w.currencyCode) &&
          CRYPTO_ALLOWED.includes(w.currencyCode.toUpperCase())
        );
        setWallets(filtered);
      } catch (err) {
        console.error('Failed to load wallets:', err);
        toast.error('Failed to load wallets');
      } finally {
        setWalletsLoading(false);
      }
    }
    fetchWallets();
  }, [currentEntity?.id, currentEntity?.code, toast]);

  // Close client menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (clientMenuRef.current && !clientMenuRef.current.contains(event.target as Node)) {
        setShowClientMenu(false);
      }
    };
    if (showClientMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showClientMenu]);

  const handleClearClient = () => {
    setSelectedClient(null);
    setValue('clientId', '');
    setShowClientMenu(false);
  };

  const cryptoWallets: WalletOption[] = useMemo(() => wallets.map(w => ({
    id: w.id,
    name: w.name,
    currency: w.currencyCode,
    icon: CURRENCY_ICONS[w.currencyCode.toUpperCase()] || '₮',
    balance: 0,
  })), [wallets]);

  const getCurrencyIn = () => {
    const w = wallets.find(x => x.id === walletInId);
    return w?.currencyCode || 'USDT';
  };
  const getCurrencyOut = () => {
    const w = wallets.find(x => x.id === walletOutId);
    return w?.currencyCode || 'USDT';
  };

  const filterNumericInput = (value: string): string => {
    let filtered = value.replace(/[^0-9.]/g, '');
    const parts = filtered.split('.');
    if (parts.length > 2) {
      filtered = parts[0] + '.' + parts.slice(1).join('');
    }
    return filtered;
  };

  const handleAmountInChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue('amountIn', filterNumericInput(e.target.value));
    clearErrors('amountIn');
  };
  const handleAmountOutChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue('amountOut', filterNumericInput(e.target.value));
    clearErrors('amountOut');
  };
  const handleWalletInChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setValue('walletInId', e.target.value);
    clearErrors('walletInId');
  };
  const handleWalletOutChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setValue('walletOutId', e.target.value);
    clearErrors('walletOutId');
  };

  const onFormSubmit = async (data: CryptoFormData) => {
    if (!currentEntity?.id) {
      toast.error('No entity selected');
      return;
    }

    setIsSubmitting(true);

    try {
      const status = data.isPendingDeal ? 'open' : 'closed';
      const currencyIn = getCurrencyIn();
      const currencyOut = getCurrencyOut();

      const rawAmountIn = parseFloat(data.amountIn || '0') || 0;
      const rawAmountOut = parseFloat(data.amountOut || '0') || 0;
      const debtInNum = parseFloat(data.debtAmountIn || '0') || 0;
      const debtOutNum = parseFloat(data.debtAmountOut || '0') || 0;

      // Totals include immediate + debt
      const totalIn = rawAmountIn + debtInNum;
      const totalOut = rawAmountOut + debtOutNum;

      // USDT/USDC → no fractional decimals (stablecoins with 1:1 USD peg)
      const getDecimals = (currency: string) =>
        (currency === 'USDT' || currency === 'USDC') ? 0 : 6;

      const transactionPayload = {
        type: 'exchange' as const,
        status: status as 'open' | 'closed',
        // Rate = amountIn / amountOut (since amounts can differ for crypto ↔ crypto)
        exchangeRate: totalOut > 0 ? (totalIn / totalOut).toFixed(8) : '1',
        amountIn: totalIn.toFixed(getDecimals(currencyIn)),
        currencyIn,
        walletInId: data.walletInId,
        amountOut: totalOut.toFixed(getDecimals(currencyOut)),
        currencyOut,
        walletOutId: data.walletOutId,
        clientId: data.clientId,
        ...(data.comment && { comment: data.comment }),
        ...(data.transactionDate && !data.isPendingDeal && { transactionDate: data.transactionDate }),
        ...(data.txHashIn && { txHashIn: data.txHashIn }),
        ...(data.txHashOut && { txHashOut: data.txHashOut }),
        ...(status === 'open' && {
          expectedAmount: data.amountOut,
          debtAmount: data.debtAmountOut || data.debtAmountIn || undefined,
          debtAmountIn: data.debtAmountIn || undefined,
          debtAmountOut: data.debtAmountOut || undefined,
          expectedPaymentDateIn: data.expectedPaymentDateIn || undefined,
          expectedPaymentDateOut: data.expectedPaymentDateOut || undefined,
        }),
      };

      await api.transactions.create(currentEntity.id, transactionPayload);

      setSuccessData({
        mode: 'CRYPTO',
        clientGivesAmount: data.amountIn,
        clientGivesCurrency: currencyIn,
        clientGetsAmount: data.amountOut,
        clientGetsCurrency: currencyOut,
        rate: totalOut > 0 ? (totalIn / totalOut).toFixed(4) : '1',
        clientId: data.clientId,
        transactionDate: data.transactionDate,
      });

      setShowSuccessModal(true);
      toast.success('Transaction created successfully');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to submit transaction');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setSuccessData(null);
    setSelectedClient(null);
    reset({
      amountIn: '',
      amountOut: '',
      walletInId: '',
      walletOutId: '',
      clientId: '',
      comment: '',
      isPendingDeal: false,
      debtAmountIn: '',
      debtAmountOut: '',
      expectedPaymentDateIn: '',
      expectedPaymentDateOut: '',
      transactionDate: new Date().toISOString().split('T')[0],
      txHashIn: '',
      txHashOut: '',
    });
    onSubmit();
  };

  const getError = (field: keyof CryptoFormData) => errors[field]?.message;

  return (
    <>
      <form
        className="grid grid-cols-1 lg:grid-cols-[1fr,340px] gap-6"
        onSubmit={handleSubmit(onFormSubmit)}
      >
        {/* Left Panel - Crypto Exchange */}
        <div>
          <div className="p-6">
            {/* Amount In - crypto */}
            <AmountInputField
              sectionLabel={t('cashier.createOperation.cryptoAmountIn', 'Сумма к получению (IN)')}
              floatingLabel="AMOUNT IN"
              balanceLabel={t('cashier.createOperation.wallet', 'Wallet')}
              balanceValue={getCurrencyIn()}
              placeholder="0"
              value={amountIn}
              onChange={handleAmountInChange}
              walletValue={walletInId}
              onWalletChange={handleWalletInChange}
              wallets={cryptoWallets}
              walletsLoading={walletsLoading}
              error={getError('amountIn')}
              walletError={getError('walletInId')}
              inputName="amountIn"
              walletName="walletInId"
              ref={amountInputRef}
            />

            {/* Total IN = immediate + debtIn (shown when pending deal) */}
            {isPendingDeal && (() => {
              const immediate = parseFloat(amountIn || '0') || 0;
              const debt = parseFloat(debtAmountIn || '0') || 0;
              const total = immediate + debt;
              const currency = getCurrencyIn();
              return (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 14px', marginTop: '-4px', marginBottom: '4px',
                  borderRadius: '8px', background: 'color-mix(in srgb, var(--ctp-teal) 8%, var(--color-surface))',
                  border: '1px dashed color-mix(in srgb, var(--ctp-teal) 40%, transparent)',
                }}>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    –
                  </span>
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '15px', color: 'var(--ctp-teal, #179299)' }}>
                    {total > 0 ? total.toFixed(0) : '—'} {currency}
                  </span>
                </div>
              );
            })()}

            {/* Arrow divider */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '8px 0', gap: '10px',
            }}>
              <div style={{ flex: 1, height: '1px', background: 'var(--color-border)' }} />
              <div style={{
                width: '32px', height: '32px', borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'var(--color-surface-hover)',
                border: '1px solid var(--color-border)',
                color: 'var(--ctp-teal, #179299)',
                fontSize: '16px', fontWeight: 700, flexShrink: 0,
              }}>↓</div>
              <div style={{ flex: 1, height: '1px', background: 'var(--color-border)' }} />
            </div>

            {/* Amount Out - crypto */}
            <AmountInputField
              sectionLabel={t('cashier.createOperation.cryptoAmountOut', 'Сумма к выдаче (OUT)')}
              floatingLabel="AMOUNT OUT"
              balanceLabel={t('cashier.createOperation.wallet', 'Wallet')}
              balanceValue={getCurrencyOut()}
              placeholder="0"
              value={amountOut}
              onChange={handleAmountOutChange}
              walletValue={walletOutId}
              onWalletChange={handleWalletOutChange}
              wallets={cryptoWallets}
              walletsLoading={walletsLoading}
              error={getError('amountOut')}
              walletError={getError('walletOutId')}
              inputName="amountOut"
              walletName="walletOutId"
              _isSecondary
            />

            {/* Total OUT = immediate + debtOut */}
            {isPendingDeal && (() => {
              const immediate = parseFloat(amountOut || '0') || 0;
              const debt = parseFloat(debtAmountOut || '0') || 0;
              const total = immediate + debt;
              const currency = getCurrencyOut();
              return (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 14px', marginTop: '-4px', marginBottom: '4px',
                  borderRadius: '8px', background: 'color-mix(in srgb, var(--ctp-teal) 8%, var(--color-surface))',
                  border: '1px dashed color-mix(in srgb, var(--ctp-teal) 40%, transparent)',
                }}>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    –
                  </span>
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '15px', color: 'var(--ctp-teal, #179299)' }}>
                    {total > 0 ? total.toFixed(0) : '—'} {currency}
                  </span>
                </div>
              );
            })()}

            {/* Date, Comment & Pending Deal */}
            <div className="mt-4 flex flex-col sm:flex-row gap-4">
              {!isPendingDeal && (
                <div className="sm:w-44">
                  <label className="text-xs font-medium text-foreground-muted uppercase tracking-wide mb-2 block">
                    {t('cashier.createOperation.date', 'Date')}
                  </label>
                  <input
                    type="date"
                    className="w-full py-2.5 px-3 border border-border rounded-lg text-sm text-foreground bg-surface focus:outline-none focus:border-ctp-teal"
                    {...register('transactionDate')}
                  />
                  <p className="text-[11px] text-foreground-muted mt-1.5">
                    {t('cashier.createOperation.dateHint', 'Optional. Defaults to today.')}
                  </p>
                </div>
              )}

              <div className="flex-1">
                <label className="text-xs font-medium text-foreground-muted uppercase tracking-wide mb-2 block">
                  {t('cashier.createOperation.comment', 'Comment')}
                </label>
                <input
                  type="text"
                  className="w-full py-2.5 px-3 border border-border rounded-lg text-sm text-foreground bg-surface focus:outline-none focus:border-ctp-teal placeholder:text-foreground-muted"
                  placeholder={t('cashier.createOperation.addNote', 'Add a note...')}
                  maxLength={200}
                  {...register('comment')}
                />
              </div>

              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-[18px] h-[18px] accent-ctp-teal cursor-pointer"
                    {...register('isPendingDeal')}
                  />
                  <span className="text-sm text-foreground whitespace-nowrap">
                    {t('cashier.createOperation.pendingDeal', 'Pending Deal')}
                  </span>
                </label>
              </div>
            </div>

            {/* TX Hash fields — two, one per crypto wallet */}
            {(() => {
              const wIn = wallets.find(w => w.id === walletInId);
              const wOut = wallets.find(w => w.id === walletOutId);
              if (!wIn && !wOut) return null;
              return (
                <div className="mt-4 flex flex-col sm:flex-row gap-4">
                  {wIn && wIn.type === 'crypto' && (
                    <div className="flex-1">
                      <TxHashField
                        label={t('cashier.txHash.labelIn', 'TX Hash (IN)')}
                        walletAddress={wIn.address}
                        walletType={wIn.type}
                        value={watch('txHashIn') || ''}
                        onChange={(v) => setValue('txHashIn', v)}
                      />
                    </div>
                  )}
                  {wOut && wOut.type === 'crypto' && (
                    <div className="flex-1">
                      <TxHashField
                        label={t('cashier.txHash.labelOut', 'TX Hash (OUT)')}
                        walletAddress={wOut.address}
                        walletType={wOut.type}
                        value={watch('txHashOut') || ''}
                        onChange={(v) => setValue('txHashOut', v)}
                      />
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Pending Deal extra fields */}
            {isPendingDeal && (
              <div className="mt-4 p-4 rounded-lg border border-ctp-peach/30 bg-ctp-peach/5 flex flex-col gap-4">
                <div className="flex gap-4">
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-2">
                      <label className="text-xs font-medium text-foreground-muted uppercase tracking-wide">
                        {t('cashier.createOperation.debtIn', 'Нам должны')}
                      </label>
                      <button
                        type="button"
                        onClick={() => setValue('debtAmountIn', amountIn)}
                        className="text-[10px] bg-ctp-surface-hover hover:bg-ctp-peach/20 text-ctp-peach px-2 py-0.5 rounded border border-ctp-peach/30 transition-colors"
                      >
                        {t('cashier.createOperation.fill', 'Fill')}
                      </button>
                    </div>
                    <div className="relative">
                      <input
                        type="text"
                        className="w-full py-2 px-3 border border-border rounded-lg text-sm text-foreground bg-surface focus:outline-none focus:border-ctp-peach"
                        placeholder="0.00"
                        {...register('debtAmountIn')}
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-foreground-muted">
                        {getCurrencyIn()}
                      </div>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-2">
                      <label className="text-xs font-medium text-foreground-muted uppercase tracking-wide">
                        {t('cashier.createOperation.debtOut', 'Мы должны')}
                      </label>
                      <button
                        type="button"
                        onClick={() => setValue('debtAmountOut', amountOut)}
                        className="text-[10px] bg-ctp-surface-hover hover:bg-ctp-peach/20 text-ctp-peach px-2 py-0.5 rounded border border-ctp-peach/30 transition-colors"
                      >
                        {t('cashier.createOperation.fill', 'Fill')}
                      </button>
                    </div>
                    <div className="relative">
                      <input
                        type="text"
                        className="w-full py-2 px-3 border border-border rounded-lg text-sm text-foreground bg-surface focus:outline-none focus:border-ctp-peach"
                        placeholder="0.00"
                        {...register('debtAmountOut')}
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-foreground-muted">
                        {getCurrencyOut()}
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Client Information */}
        <div className="bg-surface rounded-xl shadow-lg border border-border overflow-hidden flex flex-col">
          <div className="px-6 py-4 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground m-0">
              {t('cashier.createOperation.clientInformation', 'Client Information')}
            </h2>
          </div>

          <div className="p-6 flex flex-col gap-4 flex-1">
            <ClientSearchAutocomplete
              value={watch('clientId')}
              onChange={(clientId) => setValue('clientId', clientId)}
              onClientSelect={setSelectedClient}
              onClearError={() => clearErrors('clientId')}
              error={getError('clientId')}
              placeholder={t('cashier.createOperation.searchPlaceholder', 'Search...')}
            />

            {selectedClient && (
              <div className="border border-border rounded-lg p-4 bg-surface-hover/30">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-ctp-overlay0/30 flex items-center justify-center">
                      <User size={20} className="text-foreground-muted" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground">
                        {t('cashier.createOperation.client', 'Client')}
                      </div>
                      <div className="text-xs text-foreground-muted">{selectedClient.uniqueId || '-'}</div>
                    </div>
                  </div>
                  <div className="relative" ref={clientMenuRef}>
                    <button
                      type="button"
                      onClick={() => setShowClientMenu(!showClientMenu)}
                      className="text-foreground-muted hover:text-foreground p-1 -m-1 rounded hover:bg-surface-hover transition-colors"
                    >
                      <MoreVertical size={18} />
                    </button>
                    {showClientMenu && (
                      <div className="absolute right-0 top-full mt-1 w-44 bg-surface border border-border rounded-lg shadow-lg z-50 py-1 overflow-hidden">
                        <button
                          type="button"
                          onClick={handleClearClient}
                          className="w-full px-3 py-2 text-sm text-left text-ctp-red hover:bg-surface-hover flex items-center gap-2 transition-colors"
                        >
                          <X size={14} />
                          {t('cashier.createOperation.clearClient', 'Clear Selection')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-3 text-sm">
                  {selectedClient.name && (
                    <div>
                      <div className="text-xs text-foreground-muted">
                        {t('cashier.createOperation.name', 'Name')}
                      </div>
                      <div className="text-foreground">{selectedClient.name}</div>
                    </div>
                  )}
                  {selectedClient.description && (
                    <div>
                      <div className="text-xs text-foreground-muted">
                        {t('cashier.createOperation.description', 'Description')}
                      </div>
                      <div className="text-foreground">{selectedClient.description}</div>
                    </div>
                  )}
                  {selectedClient.transactionCount !== undefined && (
                    <div>
                      <div className="text-xs text-foreground-muted">
                        {t('cashier.createOperation.transactions', 'Transactions')}
                      </div>
                      <div className="text-foreground">{selectedClient.transactionCount}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <button
              type="submit"
              className="
                flex items-center justify-center gap-2.5 w-full py-4 px-6
                text-white border-none rounded-xl text-[15px] font-bold
                cursor-pointer transition-all active:scale-95 mt-auto
                shadow-lg
                disabled:opacity-60 disabled:cursor-not-allowed
                bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-700 hover:to-teal-600
              "
              disabled={isSubmitting || walletsLoading}
            >
              <Coins size={18} />
              {isSubmitting
                ? t('cashier.createOperation.processing', 'Processing...')
                : t('cashier.createOperation.crypto', 'CRYPTO')}
            </button>
          </div>
        </div>
      </form>

      <OperationSuccessModal
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
        data={successData}
      />
    </>
  );
}
